import React, { MouseEvent, ReactNode, useCallback, useMemo, useState } from "react";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import DistanceUnit from "@mapmycustomers/shared/enum/DistanceUnit";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import HasDistance from "@mapmycustomers/shared/types/base/HasDistance";
import Named from "@mapmycustomers/shared/types/base/Named";
import TypedEntity from "@mapmycustomers/shared/types/base/TypedEntity";
import { UserRef } from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import anyColorToHex from "@mapmycustomers/shared/util/color/anyColorToHex";
import { Avatar, EntityTypeShapedIcon, Popconfirm } from "@mapmycustomers/ui";

import Distance from "@app/component/Distance";
import EntityItemProperties from "@app/component/RecordCard/types/EntityItemProperties";
import RoundButton from "@app/component/RoundButton";
import ButtonType from "@app/enum/ButtonType";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";

import styles from "./BaseEntityItem.module.scss";

interface Props<T> extends EntityItemProperties<T> {
  actions?: React.ReactNode;
  actionsBar?: React.ReactNode;
  borderColor?: AnyColor | null;
  children?: ReactNode;
  className?: string;
  enableRemoveAction?: boolean;
  highlight?: boolean;
  icon?: ReactNode;
  name?: ReactNode;
  onClick?: (e: MouseEvent) => void;
  onDoubleClick?: (e: MouseEvent) => void;
  onHover?: () => void;
  onLeave?: () => void;
  onRemoveAction?: () => void;
  removeActionConfirmationText?: string;
  removeActionHoverText?: string;
  showDistance?: boolean;
}

const BaseEntityItem = <T extends Named>({
  actions,
  actionsBar,
  borderColor,
  children,
  className,
  enableRemoveAction,
  entity,
  highlight,
  icon,
  name,
  onClick,
  onDoubleClick,
  onHover,
  onLeave,
  onRemoveAction,
  removeActionConfirmationText,
  removeActionHoverText,
  showDistance,
}: Props<T>) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [removeConfirmationVisibility, setRemoveConfirmationVisibility] = useState<boolean>(false);
  const [removeTooltipVisibility, setRemoveTooltipVisibility] = useState<boolean>(false);

  const handleLeave = useCallback(() => {
    if (!removeConfirmationVisibility) {
      onLeave?.();
    }
    setRemoveTooltipVisibility(false);
  }, [onLeave, removeConfirmationVisibility, setRemoveTooltipVisibility]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick?.(e);
      analytics.clicked(["Item"], { doubleClick: false });
    },
    [analytics, onClick]
  );

  const handleDoubleClick = useCallback(
    (e: MouseEvent) => {
      onDoubleClick?.(e);
      analytics.clicked(["Item"], { doubleClick: true });
    },
    [analytics, onDoubleClick]
  );

  const handleCancel = useCallback(() => setRemoveConfirmationVisibility(false), []);

  const handleConfirm = useCallback(() => {
    onRemoveAction?.();
    setRemoveConfirmationVisibility(false);
  }, [onRemoveAction]);

  const handleActionClick = useCallback(
    (e: MouseEvent) => {
      stopEvents(e);
      setRemoveTooltipVisibility(false);
      if (!removeActionConfirmationText) {
        onRemoveAction?.();
      }
    },
    [onRemoveAction, removeActionConfirmationText, setRemoveTooltipVisibility]
  );

  const style = useMemo(
    () => ({
      borderColor: borderColor ? anyColorToHex(borderColor) : "initial",
    }),
    [borderColor]
  );

  const getRemoveIcon = useCallback(
    () => (
      <RoundButton
        className={styles.remove}
        onClick={handleActionClick}
        onMouseEnter={onHover}
        onMouseLeave={handleLeave}
        type={ButtonType.DANGER}
      >
        <FontAwesomeIcon className={styles.removeIcon} icon={faMinusCircle} />
      </RoundButton>
    ),
    [handleActionClick, handleLeave, onHover]
  );

  const visible = enableRemoveAction && highlight && !!removeActionHoverText;

  const entityType =
    entity && "entity" in entity
      ? (entity as unknown as TypedEntity<EntityType>).entity
      : undefined;
  // It is fine if entityType is not compatible, we'll just get undefined
  // @ts-ignore
  const fieldModel = entityType ? getFieldModelByEntityType(entityType) : undefined;
  const nameField = fieldModel?.getByName(
    entityType === EntityType.PERSON ? PersonFieldName.FIRST_NAME : "name"
  );
  const isNameReadable = !nameField || nameField.isReadable;

  return (
    <div className={styles.container}>
      <Popover
        autoAdjustOverflow={false}
        content={
          visible ? (
            <Tooltip
              destroyTooltipOnHide
              onOpenChange={setRemoveTooltipVisibility}
              open={removeTooltipVisibility}
              placement="left"
              title={removeActionHoverText}
              trigger={removeConfirmationVisibility ? [] : ["hover"]}
            >
              {removeActionConfirmationText ? (
                <Popconfirm
                  cancelText={intl.formatMessage({
                    id: "recordCard.remove.cancel",
                    defaultMessage: "Not Yet",
                    description: "Remove from selection quick action cancel",
                  })}
                  description={removeActionConfirmationText}
                  okText={intl.formatMessage({
                    id: "recordCard.remove.ok",
                    defaultMessage: "Remove",
                    description: "Remove from selection quick action ok",
                  })}
                  onCancel={handleCancel}
                  onConfirm={handleConfirm}
                  onOpenChange={setRemoveConfirmationVisibility}
                  open={removeConfirmationVisibility}
                  placement="bottomLeft"
                >
                  {getRemoveIcon()}
                </Popconfirm>
              ) : (
                getRemoveIcon()
              )}
            </Tooltip>
          ) : null
        }
        destroyTooltipOnHide
        getPopupContainer={(trigger: HTMLElement) => trigger.parentElement!}
        mouseLeaveDelay={0}
        open={visible}
        overlayClassName={styles.popover}
        placement="topRight"
        trigger="hover"
      >
        <div
          className={cn(styles.item, className, {
            [styles.clickable]: !!onClick,
            [styles.hasActionsBar]: !!actionsBar,
            [styles.highlight]: highlight,
          })}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onMouseEnter={onHover}
          onMouseLeave={handleLeave}
        >
          <div className={styles.border} style={style} />

          <div className={styles.header}>
            <div className={styles.name}>
              <Tooltip
                title={intl.formatMessage({
                  id: "recordCard.title.hiddenNameTooltip",
                  defaultMessage: "Protected Information",
                  description:
                    "Protected information tooltip in EntityCard title when the name field is hidden",
                })}
                trigger={!isNameReadable ? ["hover"] : []}
              >
                {name ?? entity?.name}
              </Tooltip>
            </div>
            {icon ? (
              <>{icon}</>
            ) : "entity" in entity ? (
              <div className={styles.ownerIcon}>
                {"user" in entity ? (
                  <Avatar user={(entity as unknown as { user: UserRef }).user} />
                ) : entityType ? (
                  <EntityTypeShapedIcon className={styles.icon} entityType={entityType} />
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={styles.container}>
            {children && <div className={styles.body}>{children}</div>}

            {entity && "distance" in entity && showDistance && (
              <div className={styles.distance}>
                <Tooltip
                  placement="topRight"
                  title={intl.formatMessage({
                    id: "recordCard.entity.distance",
                    defaultMessage: "Distance from current location",
                    description: "Distance from current location",
                  })}
                >
                  <div>
                    <Distance unit={DistanceUnit.MILE} value={(entity as HasDistance).distance} />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>

          {actions}

          {actionsBar &&
            React.cloneElement(actionsBar as React.ReactElement, { className: styles.actionsBar })}
        </div>
      </Popover>
    </div>
  );
};

export default BaseEntityItem;
